import type { Question } from "@/lib/questions";

/**
 * 質問選択画面（/practice/select）の並び替えロジック。
 *
 * 並び替えはフロント側で取得済みデータ（フィルタ済み一覧・スコア情報・おすすめ判定）を
 * 並べ替えるだけで完結する（バックエンド変更なし、Issue #869）。
 *
 * 比較関数は同値のとき必ず 0 を返し、JavaScript の安定ソート（ES2019 で仕様保証）に
 * 依存してマスタ既定順（入力配列の順 = ALL_QUESTIONS の順）を維持する。
 *
 * 並び基準ごとの考え方:
 *   おすすめ順         : おすすめ質問を先頭固定 → 低スコア・未挑戦を上 → 残りは既定順
 *   スコアが低い順     : スコア昇順（未挑戦は末尾）
 *   スコアが高い順     : スコア降順（未挑戦は末尾）
 *   未実施を上に       : 未挑戦を上 → 既挑戦は既定順
 */

export type SortKey =
  | "recommended"
  | "score_asc"
  | "score_desc"
  | "unpracticed_first";

export interface SortOption {
  value: SortKey;
  label: string;
  /** スコア履歴に依存する基準。未ログイン時は選択肢から除外する。 */
  requiresScore: boolean;
}

export const SORT_OPTIONS: readonly SortOption[] = [
  { value: "recommended", label: "おすすめ順", requiresScore: false },
  { value: "score_asc", label: "スコアが低い順", requiresScore: true },
  { value: "score_desc", label: "スコアが高い順", requiresScore: true },
  { value: "unpracticed_first", label: "未実施を上に", requiresScore: true },
];

export const DEFAULT_SORT_KEY: SortKey = "recommended";

type ScoreInfo = { score: number; rank: string };
type ScoreMap = Map<number, ScoreInfo> | null;

/**
 * ログイン状態に応じて選択可能な並び基準を返す。
 * 未ログイン時はスコアに依存する基準を含めない（「おすすめ順」のみ）。
 */
export function availableSortOptions(isLoggedIn: boolean): SortOption[] {
  return SORT_OPTIONS.filter((o) => isLoggedIn || !o.requiresScore);
}

/**
 * フィルタ済みの質問配列を、選択中の基準で並べ替えた新しい配列を返す。
 * 元の配列は変更しない。scoreMap が null（ゲスト / スコア取得前 / 取得失敗）の場合は
 * 全質問が未挑戦扱いとなり、おすすめ質問のみ先頭固定したうえで既定順を保つ。
 */
export function sortQuestions(
  questions: Question[],
  opts: { sortKey: SortKey; scoreMap: ScoreMap; recommendedId: number | null }
): Question[] {
  const { sortKey, scoreMap, recommendedId } = opts;

  // 練習済みなら点数、未挑戦なら null。
  const scoreOf = (q: Question): number | null => {
    const s = scoreMap?.get(q.id);
    return s ? s.score : null;
  };

  const sorted = [...questions];

  switch (sortKey) {
    case "recommended":
      sorted.sort((a, b) => {
        // おすすめ質問を最上部に固定する。
        const aRec = a.id === recommendedId ? 0 : 1;
        const bRec = b.id === recommendedId ? 0 : 1;
        if (aRec !== bRec) return aRec - bRec;
        // 続けて低スコア・未挑戦を上に浮かせる（未挑戦は最も練習を促すため最上位）。
        const aKey = scoreOf(a) ?? -1;
        const bKey = scoreOf(b) ?? -1;
        if (aKey !== bKey) return aKey - bKey;
        return 0; // 同値はマスタ既定順（安定ソート）
      });
      break;

    case "score_asc":
      sorted.sort((a, b) => {
        const av = scoreOf(a);
        const bv = scoreOf(b);
        // 未挑戦は末尾にまとめる。
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return av - bv;
      });
      break;

    case "score_desc":
      sorted.sort((a, b) => {
        const av = scoreOf(a);
        const bv = scoreOf(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return bv - av;
      });
      break;

    case "unpracticed_first":
      sorted.sort((a, b) => {
        const aUn = scoreOf(a) === null ? 0 : 1;
        const bUn = scoreOf(b) === null ? 0 : 1;
        if (aUn !== bUn) return aUn - bUn;
        return 0; // 同グループ内はマスタ既定順
      });
      break;
  }

  return sorted;
}
